import { Link, PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import React, { useContext, useEffect, useState } from "react";
import "./DPRProforma.css";
import { makePdf } from "../PrintQuotation/PrintQuotation";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { QuotationContext } from "../QuotationContext";
import { QuotationInfoType } from "../Quotation.types";
import { CountryProfileService } from "../../../services/CountryProfiles/CountryProfileService";
import { CountryProfileInfo } from "../../CountriesProfile/CountriesProfile.type";
import { priceWithCurrency, priceWithCurrencyForJapan, symbolForCurrency } from "../../../Utils/PriceWithCurrency";
import { IExtendedDetailItemEntity } from "../Common/Interfaces/IItemEntity";
import { formattedPrice, formattedPriceForJapan } from "../../../Utils/FormattedPrice";
import { useNavigate } from "react-router-dom";
import { JapanDPRProforma } from "./JapanDPRProforma";
import { GenericDPRProforma } from "./GenericDPRProforma";
import { formatDateWithPattern, getCountryDateFormat } from "../../../Utils/DateFormatUtility";
import { useLocalStorageContext } from "../../../Provider/LocalStorageProvider";
import { hasWarrantyItems } from "../../../Utils/QuotationUtils";

// Helper functions for Japan-specific formatting
const getFormattedPrice = (price: string | number, country: string): string => {
  return country === "Japan" ? formattedPriceForJapan(price) : formattedPrice(price);
};

const getPriceWithCurrency = (currency: string, price: string | number, country: string): string => {
  return country === "Japan" ? priceWithCurrencyForJapan(currency, price) : priceWithCurrency(currency, price);
};

// Warranty Consent Messages Component
interface WarrantyConsentMessagesProps {
  country: string | undefined;
  hasWarranty: boolean;
}

const WarrantyConsentMessages: React.FC<WarrantyConsentMessagesProps> = ({ country, hasWarranty }) => {
  if (!hasWarranty) return null;

  return (
    <>
      {country === "Austria" && (
        <p style={{ marginTop: "15px" }}>
          <strong>Austria Electronic (E)Consent:</strong> I hereby expressly give my consent to receive documentation and communications associated with the Microsoft Protection Plan through electronic means. I can at any time revoke such consent or provide another e-mail address to be used. Likewise, the insurer has the right to revoke its consent to e-communication. Both parties to the insurance contract still have the right to send declarations, communications and information on paper. I have the right to ask for hardcopies of any declarations or information received electronically once free of charge.
        </p>
      )}
      {country === "Italy" && (
        <p style={{ marginTop: "15px" }}>
          <strong>Italy Terms & Conditions (T&C) Consent:</strong> "I confirm that I have read, and I agree to the Terms and Conditions of the Microsoft Protection Plan".
        </p>
      )}
    </>
  );
};

// Helper function for country-specific date formatting
const formatDateForCountry = async (dateStr: string | undefined, countryName: string | undefined): Promise<string> => {
  if (!dateStr) return '';
  
  try {
    // Parse the date string
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateStr}`);
      return dateStr || '';
    }
    
    // Get country's preferred date format
    let dateFormat = 'DD/MM/YYYY'; // Default format
    if (countryName) {
      const countryFormat = await getCountryDateFormat(countryName);
      if (countryFormat) {
        dateFormat = countryFormat;
      }
    }
    
    // Format the date according to country preference
    return formatDateWithPattern(date, dateFormat);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateStr || '';
  }
};
export const DPRProforma: React.FunctionComponent = () => {
  const navigate = useNavigate();
  // Use a variable for Singapore country name to ensure case-sensitive comparison
  const SINGAPORE_COUNTRY = "Singapore";
  const appInsights = useAppInsightsContext();
  const [isLoading, setIsLoading] = useState(false);
  const { selectedLanguage } = useLocalStorageContext();
  const [termsAndConditionsDocument, settermsAndConditionsDocument] = useState(
    new Uint8Array()
  );
  const [termsAndConditionsDocumentId, settermsAndConditionsDocumentId] =
    useState("");
  const { quotationInfo } = useContext(QuotationContext) as QuotationInfoType;
  const [countryProfileInfo, setCountryProfileInfo] =
    useState<CountryProfileInfo>();
  
  // State for formatted dates
  const [formattedQuotationDate, setFormattedQuotationDate] = useState<string>('');
  
  // Format dates according to customer's country preference
  useEffect(() => {
    const formatDates = async () => {
      try {
        const countryName = quotationInfo?.customer?.country;
        
        // Format quotation date
        if (quotationInfo?.quotationDate) {
          const formatted = await formatDateForCountry(quotationInfo.quotationDate, countryName);
          setFormattedQuotationDate(formatted);
        }
      } catch (error) {
        console.error('Error formatting dates:', error);
      }
    };

    formatDates();
  }, [quotationInfo?.quotationDate, quotationInfo?.customer?.country]);
  
  const lineItems: IExtendedDetailItemEntity[] =
    quotationInfo?.bundleItems?.length > 0
      ? ([
        ...quotationInfo?.standaloneItems,
        ...quotationInfo?.bundleItems,
      ] as IExtendedDetailItemEntity[])
      : quotationInfo?.standaloneItems?.length > 0
        ? ([
          ...quotationInfo?.standaloneItems,
          ...quotationInfo.bundleItems,
        ] as IExtendedDetailItemEntity[])
        : [];
  
  // Check if cart contains warranty items
  const hasWarranty = hasWarrantyItems(lineItems);
  
  const paymentTermOption = quotationInfo.paymentMethod === "WireTransfer";
  const pdfFileName = `${quotationInfo?.quotationNumber}_DPR`;
  const PrintHtmlToPdf = async () => {
    appInsights.trackEvent({
      name: "In DPRProforma.tsx, Clicked Print button to download the DPR Proforma",
    });
    if (
      termsAndConditionsDocument.byteLength === 0 &&
      termsAndConditionsDocumentId
    ) {
      await CountryProfileService.getCountryProfileDocument(
        termsAndConditionsDocumentId
      ).then((res) => {
        appInsights.trackEvent({
          name: "In DPRProforma.tsx, Country Terms and Condition document fetched successfully.",
        });
        if (res.data === null) {
          makePdf(
            appInsights,
            pdfFileName,
            lineItems.length,
            termsAndConditionsDocument
          );
        }
        settermsAndConditionsDocument(res.data);
      }).catch((error) => {
        makePdf(
          appInsights,
          pdfFileName,
          lineItems.length,
          termsAndConditionsDocument
        );
        setIsLoading(false);
        appInsights.trackException({
          error,
          properties: {
            name: `In DPRProforma.tsx, error while fetching termsAndCondition document.`,
          },
        });
      });
    } else {
      makePdf(
        appInsights,
        pdfFileName,
        lineItems.length,
        termsAndConditionsDocument
      );
    }
  };
  useEffect(() => {
    async function PrintPdf() {
      try {
        if (termsAndConditionsDocument.byteLength > 0) {
          setIsLoading(true);
          await makePdf(
            appInsights,
            pdfFileName,
            lineItems.length,
            termsAndConditionsDocument
          );
          setIsLoading(false);
          settermsAndConditionsDocument(new Uint8Array());
        }
      } catch (error) {
        setIsLoading(false);
      }
    }
    PrintPdf();
  }, [termsAndConditionsDocument.byteLength]);
  useEffect(() => {
    setIsLoading(true);
    CountryProfileService.getCountryProfileByCountryName(quotationInfo.country)
      .then(async (res) => {
        if (res !== undefined) {
          setCountryProfileInfo(res.data.data.data);
          settermsAndConditionsDocumentId(
            res.data?.data?.data?.termsAndConditions[
              res.data?.data?.data?.termsAndConditions?.length - 1
            ]?.id
          );
          appInsights.trackEvent({
            name: "In DPRProforma.tsx, country detailed fetched successfully.",
          });
          setIsLoading(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        appInsights.trackException({
          error,
          properties: {
            name: "In DPRProforma.tsx, error while getting all country detail.",
          },
        });
      });
  }, []);
  if (isLoading) {
    return <Spinner size={SpinnerSize.large} />;
  }

  // Use Japanese template for Japan DPR only when Japanese language is selected
  // Read directly from localStorage to get the most up-to-date language value
  // This fixes the issue where context might not have updated yet when navigating from search
  const currentLanguageFromStorage = localStorage.getItem("selectedLanguage") || selectedLanguage;
  if (quotationInfo?.country === "Japan" && currentLanguageFromStorage === "ja") {
    return <JapanDPRProforma />;
  }

  // Use Generic template for all countries with language switching support
  // This template now supports all countries with localization
  // Pass currentLanguageFromStorage to ensure the most up-to-date language is used
  if (quotationInfo?.country) {
    return <GenericDPRProforma country={quotationInfo.country} selectedLanguage={currentLanguageFromStorage} />;
  }

  const backButtonClick = () => {
    appInsights.trackEvent({
      name: "In DPRProforma.tsx, Clicked Back button.",
      properties: { quotationId: quotationInfo.quotationId },
    });
    navigate("/QuotationDetail", {
      state: {
        searchText: quotationInfo.quotationId,
        partitionKey: quotationInfo.partitionKey,
      },
    });
  };


  return (
    <div>
      <div className="btn_download">
        <PrimaryButton className="margin-right5" onClick={backButtonClick}>
          Back
        </PrimaryButton>
        <PrimaryButton onClick={PrintHtmlToPdf}>
          Download DPR Proforma PDF
        </PrimaryButton>

      </div>
      <div style={{ padding: "0 40px" }}>
        <div id={pdfFileName}>
          <div className={`flex space-between ${window.innerWidth > 750 ? 'flexDirectionRow' : 'flexDirectionColumn'} width100`}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: quotationInfo?.country === "Japan" ? "30px" : undefined }}>
              <div>
                <img
                  src="/image.png"
                  className="invlogo"
                  style={{ width: "150px", marginBottom: "10px" }}
                  alt="Microsoft logo"
                />
                <p className="pb-1">
                  {countryProfileInfo?.administrativeOriginName}
                </p>
                <p>{countryProfileInfo?.administrativeOriginStreet}</p>
                <p className="pb-1">{countryProfileInfo?.administrativeOriginCity}
                  {countryProfileInfo?.administrativeOriginState ? `, ${countryProfileInfo.administrativeOriginState} ` : " "}
                  {countryProfileInfo?.administrativeOriginCountry === SINGAPORE_COUNTRY ? "" : countryProfileInfo?.administrativeOriginZipCode}
                </p>
                <p className="pb-1">
                  {countryProfileInfo?.administrativeOriginCountry}
                  {countryProfileInfo?.administrativeOriginCountry === SINGAPORE_COUNTRY ? ` ${countryProfileInfo?.administrativeOriginZipCode}` : ""}
                </p>
                {countryProfileInfo?.vatId && (
                  <p className="pb-1">
                    <b>VAT Registration:</b>
                    {` ${countryProfileInfo?.vatId}`}
                  </p>
                )}
              </div>
            </div>
            <div style={{ marginTop: "10px" }}>
              <div>
                <h2
                  className="pb-1"
                  style={{ textAlign: "right", fontSize: "24px" }}
                >
                  Down Payment Request
                </h2>
              </div>
              <div>

                  <p className="pb-1">
                    <b>Request Created Date:</b>
                    {` ${formattedQuotationDate || quotationInfo?.quotationDate}`}
                  </p>
                  <p className="pb-1">
                    <b>Quotation ID:</b>
                    {` ${quotationInfo?.quotationNumber}`}
                  </p>
                  <p className="pb-1">
                    <b>PO Number:</b>
                    {` ${quotationInfo?.customerPurchaseOrder
                      ? quotationInfo?.customerPurchaseOrder
                      : ""
                      }`}
                  </p>
                  <p className="pb-1">
                    <b>Page #</b>
                    {` 1 of 1`}
                  </p>
              </div>
            </div>
          </div>
          <div className="horizontalLine"></div>
          <div className={`top30 flex space-between ${window.innerWidth > 750 ? 'flexDirectionRow' : 'flexDirectionColumn'}`}>
            <div className={`${window.innerWidth > 750 ? 'width33' : ''}`}>
              <table className="table-no-border">
                <tbody>
                  <tr>
                    <td className="td-border">
                      <p className="pb-1">
                        <b>Bill To:</b>
                      </p>
                      <p className="pb-1">
                        {`${quotationInfo?.customer?.selectedBillToAddress?.name}`}
                      </p>
                      <p className="pb-1">
                        {`${quotationInfo?.customer?.selectedBillToAddress?.street}`}
                      </p>
                      <p className="pb-1">
                        {`${quotationInfo?.customer?.selectedBillToAddress?.city}, ${quotationInfo?.customer?.selectedBillToAddress?.state}${quotationInfo?.customer?.selectedBillToAddress?.country === SINGAPORE_COUNTRY ? '' : ' ' + quotationInfo?.customer?.selectedBillToAddress?.postalCode}`}
                      </p>
                      <p className="pb-1">
                        {quotationInfo?.customer?.selectedBillToAddress?.country}
                        {quotationInfo?.customer?.selectedBillToAddress?.country === SINGAPORE_COUNTRY ? ` ${quotationInfo?.customer?.selectedBillToAddress?.postalCode}` : ''}
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className={`${window.innerWidth > 750 ? 'width33' : ''}`}>
              <p className="pb-1">
                <b>Customer:</b>
                {` ${quotationInfo?.customer?.accountId}`}
              </p>
              <p className="pb-1">
                <b>
                  Customer{" "}
                  {lineItems[0]?.taxType !== null
                    ? lineItems[0]?.taxType
                    : "Tax"}{" "}
                  ID:
                </b>
                {` ${quotationInfo?.customer?.taxId}`}
              </p>
              <p className="pb-1">
                <b>Sold To ID:</b>
                {` ${quotationInfo?.customer?.businessAddress?.sapAccountId || ""}`}
              </p>
            </div>
            <div className={`${window.innerWidth > 750 ? 'width33' : ''}`}>
              <table className="table-no-border">
                <tbody>
                  <tr>
                    <td className="td-border">
                      <p className="pb-1">
                        <b>Ship To:</b>
                      </p>
                      <p className="pb-1">
                        {`${quotationInfo?.customer?.selectedShipToAddress?.name}`}
                      </p>
                      <p className="pb-1">
                        {quotationInfo?.customer?.selectedShipToAddress?.street}
                      </p>
                      <p className="pb-1">
                        {`${quotationInfo?.customer?.selectedShipToAddress?.city}, ${quotationInfo?.customer?.selectedShipToAddress?.state}${quotationInfo?.customer?.selectedShipToAddress?.country === SINGAPORE_COUNTRY ? '' : ' ' + quotationInfo?.customer?.selectedShipToAddress?.postalCode}`}
                      </p>
                      <p className="pb-1">
                        {quotationInfo?.customer?.selectedShipToAddress?.country}
                        {quotationInfo?.customer?.selectedShipToAddress?.country === SINGAPORE_COUNTRY ? ` ${quotationInfo?.customer?.selectedShipToAddress?.postalCode}` : ''}
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="row" style={{ marginTop: "50px" }}>
            <table className="table border-color">
              <tr className="table-header">
                <th>#</th>
                <th>Product Name</th>
                <th>Qty</th>
                {lineItems[0]?.taxType ? (
                  <th className="text-right">Price</th>
                ) : (
                  <th className="text-right">MSRP</th>
                )}
                {lineItems[0]?.taxType ? (
                  <th className="text-right">
                    Extended Line {lineItems[0]?.taxType}
                  </th>
                ) : (
                  <th className="text-right">Extended Line Tax</th>
                )}
                {quotationInfo?.customer?.region === "EMEA" && (
                  <th className="text-right">Extended Copyright Levy Fee{Number(quotationInfo?.totalFee ?? 0) > 0 ? '*' : ''}</th>
                )}
                <th className="text-right">Line Total</th>
              </tr>
              {lineItems?.map((product, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>
                    [{product.sapItemId}]{product.productTitle}
                  </td>
                  <td>{product.quantity}</td>
                  <td className="text-right">
                    {getFormattedPrice(product?.msrp, quotationInfo?.country)}
                  </td>
                  <td className="text-right">
                    {getFormattedPrice(product?.lineItemTax, quotationInfo?.country)}
                  </td>
                  {quotationInfo?.customer?.region === "EMEA" && (
                    <td className="text-right">
                      {getFormattedPrice(product?.levyFeeAmount, quotationInfo?.country)}
                    </td>
                  )}
                  <td className="text-right">
                    {getFormattedPrice(product?.lineTotalWithTax, quotationInfo?.country)}
                  </td>
                </tr>
              ))}
            </table>
          </div>
          <div className="below-table">
            <div>
              {quotationInfo?.customer?.region === "EMEA" && (
                <tr>
                  <td colSpan={6} className="tDataWithoutBorder">
                    <strong>Copyright Levy Fee:</strong>
                  </td>
                  <td className="text-right tDataWithoutBorder">
                    {getPriceWithCurrency(
                      quotationInfo?.quoteCurrency,
                      quotationInfo.totalFee ?? 0,
                      quotationInfo?.country
                    )}
                  </td>
                </tr>
              )}
              <tr>
                <td colSpan={6} className="tDataWithoutBorder">
                  <strong>
                    {["Japan", "New Zealand", "Singapore"].includes(quotationInfo?.country)
                      ? "Subtotal:"
                      : "Subtotal (including copyright levy):"}
                  </strong>
                </td>
                <td className="text-right tDataWithoutBorder">
                  {getPriceWithCurrency(
                    quotationInfo?.quoteCurrency,
                    quotationInfo.subTotal,
                    quotationInfo?.country
                  )}
                </td>
              </tr>
              <tr>
                    <td colSpan={6} className="tDataWithoutBorder">
                      <strong>{lineItems.length > 0 && lineItems[0]?.taxType !== null ? lineItems[0]?.taxType : 'Tax'} Amount ({lineItems.length > 0 && lineItems[0]?.taxType !== null ? lineItems[0]?.taxType : 'Tax'} Rate):</strong>
                    </td>
                    <td colSpan={1} className="text-right tDataWithoutBorder">
                      {getPriceWithCurrency(
                        quotationInfo?.quoteCurrency,
                        (quotationInfo?.totalTax) ? quotationInfo?.totalTax : 0,
                        quotationInfo?.country
                      )}
                      {" "}
                      ({(lineItems.find(item => item?.effectiveTaxRate > 0)?.effectiveTaxRate || 0) * 100}%)
                    </td>
                  </tr>
              <tr>
                <td colSpan={6} className="tDataWithoutBorder">
                  <strong>Total Amount{quotationInfo?.country === SINGAPORE_COUNTRY ? " (inclusive of GST)" : ""}:</strong>
                </td>
                <td colSpan={1} className="text-right tDataWithoutBorder">
                  {getPriceWithCurrency(
                    quotationInfo?.quoteCurrency,
                    quotationInfo.total,
                    quotationInfo?.country
                  )}
                </td>
              </tr>
            </div>
          </div>
          <div
            style={{
              marginTop: "50px",
              display: "inline-flex",
            }}
          >
            <p>
              <strong className="pb-5">Comments:</strong>
              <p>{quotationInfo?.comments}</p>
              {` Your timely payment is greatly appreciated using the below payment details. Reminder that Check Payment is no longer an 
                acceptable form of payment. Microsoft will ship the item(s) as soon as we receive your full payment. Thank you for your business.`}
            </p>
          </div>
          {paymentTermOption ? (
            <>
              <div style={{ marginTop: "10px" }}>
                <p className="pb-5">
                  <strong>Payment Option for Wire Transfer:</strong>
                </p>
              </div>
              <div>
                <div>
                  <div>
                    {countryProfileInfo?.bankName && (
                      <p className="pb-5">
                        <b>{`Bank Name: `}</b>
                        {`${countryProfileInfo?.bankName}`}
                      </p>
                    )}
                    {countryProfileInfo?.bankAddress && (
                      <p className="pb-5">
                        <b>{`Bank Address: `}</b>
                        {`${countryProfileInfo?.bankAddress}`}
                      </p>
                    )}
                    {countryProfileInfo?.bankPhone && (
                      <p className="pb-5">
                        <b>{`Bank Phone: `}</b>
                        {`${countryProfileInfo?.bankPhone}`}
                      </p>
                    )}
                    {countryProfileInfo?.addressLine1 && (
                      <p className="pb-5">
                        <b>{`Address Line 1: `}</b>
                        {`${countryProfileInfo?.addressLine1}`}
                      </p>
                    )}
                    {countryProfileInfo?.addressLine2 && (
                      <p className="pb-5">
                        <b>{`Address Line 2: `}</b>
                        {`${countryProfileInfo?.addressLine2}`}
                      </p>
                    )}
                    {countryProfileInfo?.addressLine3 && (
                      <p className="pb-5">
                        <b>{`Address Line 3: `}</b>
                        {`${countryProfileInfo?.addressLine3}`}
                      </p>
                    )}
                    {countryProfileInfo?.accountName && (
                      <p className="pb-5">
                        <b>{`Account Name: `}</b>
                        {`${countryProfileInfo?.accountName}`}
                      </p>
                    )}
                    {countryProfileInfo?.accountAddress && (
                      <p className="pb-5">
                        <b>{`Account Address: `}</b>
                        {`${countryProfileInfo?.accountAddress}`}
                      </p>
                    )}
                    {countryProfileInfo?.accountNumber && (
                      <p className="pb-5">
                        <b>{`Account Number: `}</b>
                        {`${countryProfileInfo?.accountNumber}`}
                      </p>
                    )}
                    {countryProfileInfo?.iban && (
                      <p className="pb-5">
                        <b>{`IBAN Number: `}</b>
                        {`${countryProfileInfo?.iban}`}
                      </p>
                    )}
                    {countryProfileInfo?.swiftCode && (
                      <p className="pb-5">
                        <b>{`Swift Code: `}</b>
                        {`${countryProfileInfo?.swiftCode}`}
                      </p>
                    )}
                    {countryProfileInfo?.swiftBicCode && (
                      <p className="pb-5">
                        <b>{`Swift / BIC Code: `}</b>
                        {`${countryProfileInfo?.swiftBicCode}`}
                      </p>
                    )}
                    {countryProfileInfo?.switchCode && (
                      <p className="pb-5">
                        <b>{`Switch Code: `}</b>
                        {`${countryProfileInfo?.switchCode}`}
                      </p>
                    )}
                    {countryProfileInfo?.sortCode && (
                      <p className="pb-5">
                        <b>{`Sort Code: `}</b>
                        {`${countryProfileInfo?.sortCode}`}
                      </p>
                    )}
                    {countryProfileInfo?.correspondingBank && (
                      <p className="pb-5">
                        <b>{`Corresponding Bank: `}</b>
                        {`${countryProfileInfo?.correspondingBank}`}
                      </p>
                    )}
                    {
                      countryProfileInfo?.vatId && (
                        <p className="pb-5">
                          <b>{`VAT: `}</b>
                          {`${countryProfileInfo?.vatId}`}
                        </p>
                      )
                    }
                    {
                      countryProfileInfo?.correspondingBankSwiftBICCode && (
                        <p className="pb-5">
                          <b>{`Corresponding Bank Swift / BIC Code: `}</b>
                          {`${countryProfileInfo?.correspondingBankSwiftBICCode}`}
                        </p>
                      )
                    }
                    {
                      countryProfileInfo?.bankKey && (
                        <p className="pb-5">
                          <b>{`Bank Key: `}</b>
                          {`${countryProfileInfo?.bankKey}`}
                        </p>
                      )
                    }
                    {
                      countryProfileInfo?.bcCode && (
                        <p className="pb-5">
                          <b>{`BC Code: `}</b>
                          {`${countryProfileInfo?.bcCode}`}
                        </p>
                      )
                    }
                    {
                      countryProfileInfo?.remittanceCurrencyCode && (
                        <p className="pb-5">
                          <b>{`Currency Code: `}</b>
                          {`${countryProfileInfo?.remittanceCurrencyCode}`}
                        </p>
                      )
                    }
                    {
                      countryProfileInfo?.remittanceCompanyCode && (
                        <p className="pb-5">
                          <b>{`Company Code: `}</b>
                          {`${countryProfileInfo?.remittanceCompanyCode}`}
                        </p>
                      )
                    }
                  </div>
                </div>
                <p className="pb-1">
                  When remitting payment to Microsoft, please reference the
                  Customer Number for prompt settlement.
                </p>
              </div>
              <div style={{ marginTop: "10px", paddingBottom: "50px" }}>
                <div>
                  <div>
                    <p className="pb-5">
                      <strong>Terms & Conditions:</strong>
                    </p>
                    <p className="pb-1">
                      Please refer to your purchase agreement for payment terms.
                      Acceptable payment method is Wire Transfer. Payment should
                      only be made by Electronic Funds Transfer. Do not send any
                      physical payment to any address on this invoice. Late or
                      incomplete payment may result in penalties. For questions
                      about this invoice, please contact Retail Stores Invoice
                      support via email at:&nbsp;
                      <a href="#">rsinvsup@microsoft.com</a>
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{ marginTop: "10px" }}>
                <div>
                  <div>
                    <p className="pb-1">
                      <b>For Credit Card Payment Options:</b>
                    </p>
                    <p className="pb-1">
                      If you advised your seller that payment by credit card is
                      your preferred payment method, please complete payment
                      using the link provided: &nbsp;
                      <Link
                        onClick={() => {
                          const url = quotationInfo.adyenPaymentLink;
                          const formattedUrl = /^https?:\/\//.test(url)
                            ? url
                            : `https://${url}`;
                          window.open(
                            formattedUrl,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }}
                        target="_blank"
                        underline
                      >
                        {quotationInfo.adyenPaymentLink}
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: "10px" }}>
                <div>
                  <div>
                    <p className="pb-1">
                      <b>Terms & Conditions:</b>
                    </p>
                    <p className="pb-1">
                      Please refer to your purchase agreement for payment terms.
                      Acceptable payment method is via Credit Card. Do not send
                      any physical payment to any address on this invoice. Late
                      or incomplete payment may result in penalties. For
                      questions about this invoice, please contact Retail Stores
                      Invoice support via email at: &nbsp;
                      <a href="#">rsinvsup@microsoft.com</a>
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
          {/* Warranty Consent Messages - rendered once for both payment methods */}
          <WarrantyConsentMessages country={quotationInfo?.country} hasWarranty={hasWarranty} />
        </div>
      </div>
    </div>
  );
};
